import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIonRouter } from "@ionic/react";
import { toast } from "sonner";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock3,
} from "lucide-react";
import { ProgramDataProxy } from "@entities/program/api/data.proxy";
import { ProgramExecutionProxy } from "@entities/program/api/execution.proxy";
import {
  scheduleTrainingListsInvalidation,
  type Training,
} from "@entities/training";
import { WeekCalendar } from "@pages/program/ui/WeekCalendar";
import {
  ProgramCompactHeader,
  ProgramCompactHeaderSentinel,
  useProgramCompactHeaderVisibility,
} from "@pages/program/ui/ProgramCompactHeader";
import { YellowFilledButton } from "@shared/styles/YellowFilledButton";
import { BlueTransparentButton } from "@shared/styles/BlueTransparentButton";
import { DefaultPage } from "@shared/layout/DefaultPage";
import { ProgramWeek } from "@entities/program/types/base";
import { useStartTrainingDeferred } from "@shared/hooks/trainingsNavigation/startTraining";
import { CustomLoader } from "@shared/animations/loaders/CustomLoader";
import RelaxImage from "@assets/icons/relaxIcon.png";
import DefaultProgramPic from "@assets/images/DefaultProgramPic.png";
import { ROUTES } from "@shared/constants/routes";
import { getDaysLeftAmount } from "@entities/program/lib/daysLeftAmount";
import { getWeeksAmount } from "@entities/program/lib/weeksAmount";
import {
  getProgramDayDate,
  getProgramWeekStartDate,
} from "@entities/program/lib/getWeekDates";
import { getMediaRoleUrl, getMediaUrl } from "@shared/helpers/getMediaUrl";
import { type WorkoutMedia } from "@entities/exercise";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const isPresent = <T,>(value: T | null | undefined): value is T =>
  value != null;

const FUTURE_WEEK_MESSAGE = "Не торопитесь, сначала закончите текущую неделю";
const FUTURE_DAY_START_WARNING =
  "Если начнёте эту тренировку, предыдущие незавершённые тренировки будут закрыты в программе.";
const REST_DAY_ACTION_MESSAGE =
  "Сегодня день отдыха. Восстановитесь, а к тренировкам вернёмся в следующий тренировочный день.";
const FUTURE_DAY_ACTION_MESSAGE =
  "Сначала завершите предыдущую тренировку в программе.";
const ENROLLMENT_QUERY_STALE_TIME_MS = 30 * 1000;

const getSafeDayValue = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const dayNumber = Number(value);
  if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 7) {
    return null;
  }

  return dayNumber.toString();
};

const getRequestedDayValue = (search: string): string | null =>
  getSafeDayValue(new URLSearchParams(search).get("day"));

const getWorkoutHistoryId = (
  day: ProgramWeek["days"][number] | null | undefined,
): number | null => {
  return (
    day?.workout_history_id ?? day?.workout?.history?.id_workout_history ?? null
  );
};

const getDateKey = (date: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getWeekDayDate = (
  day: ProgramWeek["days"][number],
  weekStartAt: string | null | undefined,
): Date | null =>
  getProgramDayDate({
    calendarDate: day.calendar_date,
    dayNum: day.day_num,
    dueAt: day.due_at,
    weekStartAt,
  });

const getExercisePreviewUrl = (
  media?: WorkoutMedia | null,
): string | undefined => {
  return getMediaRoleUrl(media, ["cover", "icon", "muscle_groups"]);
};

const getExerciseTarget = (
  exercise: NonNullable<
    ProgramWeek["days"][number]["workout"]
  >["exercises"][number],
) => {
  if (exercise.exercise_detail?.type === "cardio") {
    const seconds = exercise.duration ?? 0;
    const minutes = Math.floor(seconds / 60);
    const restSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${restSeconds.toString().padStart(2, "0")}`;
  }

  return `${exercise.reps ?? 0} x ${exercise.sets ?? 0} раз`;
};

const getWorkoutDurationMinutes = (
  exercises:
    | NonNullable<ProgramWeek["days"][number]["workout"]>["exercises"]
    | null,
) => {
  if (!exercises?.length) {
    return 0;
  }

  const seconds = exercises.reduce((total, exercise) => {
    const exerciseType = exercise.exercise_detail?.type ?? "strength";
    const workSeconds =
      exerciseType === "cardio"
        ? (exercise.duration ?? 30)
        : (exercise.sets ?? 1) * 45;
    return total + workSeconds + (exercise.rest_time ?? 30);
  }, 0);

  return Math.max(1, Math.round(seconds / 60));
};

export const ProgramEnrollmentWeekPage = () => {
  const { enrollmentId, weekNumber } = useParams<{
    enrollmentId: string;
    weekNumber: string;
  }>();
  const location = useLocation();
  const [selectedDayValue, setSelectedDayValue] = useState<string>(
    () => getRequestedDayValue(location.search) ?? "1",
  );
  const dayActionInFlightRef = useRef(false);
  const [pendingDayAction, setPendingDayAction] = useState<
    "start" | "skip" | null
  >(null);
  const [isStartWarningOpen, setIsStartWarningOpen] = useState(false);
  const {
    isCompactHeaderVisible,
    compactHeaderRootRef,
    compactHeaderSentinelRef,
  } = useProgramCompactHeaderVisibility();
  const nav = useIonRouter();
  const queryClient = useQueryClient();
  const isWeekRouteActive =
    location.pathname === `/program/${enrollmentId}/week/${weekNumber}`;

  const startTraining = useStartTrainingDeferred();
  const requestedWeekNumber = Number(weekNumber);
  const isValidWeekNumber =
    Number.isInteger(requestedWeekNumber) && requestedWeekNumber > 0;

  const { data: program } = useQuery({
    queryKey: ["enrollment", enrollmentId],
    queryFn: async () => {
      return ProgramDataProxy.getEnrollmentById(+enrollmentId);
    },
    enabled: Boolean(enrollmentId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: ENROLLMENT_QUERY_STALE_TIME_MS,
  });

  const isProgramFinished =
    (program?.statistics?.progress_percentage ?? 0) >= 100 ||
    program?.status === "completed";
  const isFutureWeek =
    Boolean(program) &&
    isValidWeekNumber &&
    !isProgramFinished &&
    requestedWeekNumber > (program?.pointer_week ?? 0);
  const programWeekQueryKey = [
    "programWeek",
    enrollmentId,
    `weekNumber/${weekNumber}`,
  ];

  const { data } = useQuery({
    queryKey: programWeekQueryKey,
    queryFn: async () => {
      return await ProgramDataProxy.getWeekInfo(enrollmentId, weekNumber);
    },
    enabled: Boolean(program) && isValidWeekNumber && !isFutureWeek,
  });

  const weekDays = Array.isArray(data?.days) ? data.days.filter(isPresent) : [];
  const safeWeekData: ProgramWeek = data
    ? {
        ...data,
        week_start_at:
          data.week_start_at ||
          getProgramWeekStartDate(
            program?.started_at,
            requestedWeekNumber,
          )?.toISOString() ||
          "",
        days: weekDays,
      }
    : {
        enrollmentId,
        week_num: weekNumber,
        status: "active",
        week_start_at: "",
        days: [],
        summary: null,
      };
  const selectedDay =
    weekDays.find((day) => day.day_num.toString() === selectedDayValue) ?? null;
  const selectedDayWorkout =
    selectedDay && selectedDay.workout_id !== null ? selectedDay : null;
  const selectedWorkoutExercises = Array.isArray(
    selectedDayWorkout?.workout?.exercises,
  )
    ? selectedDayWorkout.workout.exercises.filter(isPresent)
    : null;
  const isSelectedDayFinished =
    selectedDay?.status === "completed" || selectedDay?.status === "skipped";
  const selectedDayNumber = Number(selectedDayValue);
  const selectedDayDateKey = selectedDay
    ? getDateKey(getWeekDayDate(selectedDay, safeWeekData.week_start_at))
    : null;
  const todayDateKey = getDateKey(new Date());
  const isSelectedDayToday =
    selectedDayDateKey !== null &&
    todayDateKey !== null &&
    selectedDayDateKey === todayDateKey;
  const activeDayNum = (() => {
    const sortedDays = [...weekDays].sort((a, b) => a.day_num - b.day_num);

    for (const day of sortedDays) {
      if (day.status !== "completed" && day.status !== "skipped") {
        return day.day_num;
      }
    }

    return null;
  })();
  const isSelectedDayPast =
    Boolean(program) &&
    isValidWeekNumber &&
    (requestedWeekNumber < (program?.pointer_week ?? 0) ||
      (requestedWeekNumber === program?.pointer_week &&
        selectedDayNumber < (activeDayNum ?? 1)));

  const isActiveDay =
    activeDayNum !== null &&
    Boolean(program) &&
    isValidWeekNumber &&
    !isProgramFinished &&
    requestedWeekNumber === program?.pointer_week &&
    selectedDayNumber === activeDayNum;
  const isSelectedDayUnavailableFuture =
    activeDayNum !== null &&
    Boolean(program) &&
    isValidWeekNumber &&
    !isProgramFinished &&
    requestedWeekNumber === program?.pointer_week &&
    selectedDayNumber > activeDayNum;
  const isSelectedDayHistorical =
    Boolean(selectedDayWorkout) && (isSelectedDayFinished || isSelectedDayPast);
  const isSelectedDayStarted =
    !isSelectedDayHistorical &&
    (selectedDayWorkout?.status === "in_progress" ||
      selectedDayWorkout?.workout?.history?.status === "in_progress" ||
      (Boolean(selectedDayWorkout?.workout_history_id) &&
        !isSelectedDayFinished));
  const startWorkoutTitle = isSelectedDayHistorical
    ? "Завершено"
    : isSelectedDayStarted
      ? "Продолжить"
      : "Начать";
  const selectedDayRedirectUrl = ROUTES.program.week
    .replace(":enrollmentId", enrollmentId)
    .replace(":weekNumber", weekNumber);
  const weeksAmount = getWeeksAmount(program);
  const daysLeft = program
    ? getDaysLeftAmount(weeksAmount, program.pointer_week, program.pointer_day)
    : Math.max(
        0,
        weekDays.filter(
          (day) => day.status !== "completed" && day.status !== "skipped",
        ).length,
      );
  const progressPercentage =
    program?.statistics?.progress_percentage ??
    Math.round(
      (weekDays.filter(
        (day) => day.status === "completed" || day.status === "skipped",
      ).length /
        Math.max(weekDays.length, 1)) *
        100,
    );
  const clampedProgressPercentage = Math.min(
    Math.max(progressPercentage, 0),
    100,
  );
  const progressBorderClipRight = `${100 - clampedProgressPercentage}%`;
  const programTitle =
    data?.program_name || program?.program_name || "Программа тренировок";
  const programFormat = `${weekDays.length || 7}x${weeksAmount || weekNumber}`;
  const selectedWeekLabel = `Неделя ${requestedWeekNumber} из ${weeksAmount || requestedWeekNumber}`;
  const selectedWeekShortLabel = `Неделя ${requestedWeekNumber}/${weeksAmount || requestedWeekNumber}`;
  const heroImage =
    getMediaUrl(data?.media?.cover) ||
    getMediaUrl(program?.media?.cover) ||
    DefaultProgramPic;
  const workoutDurationMinutes = getWorkoutDurationMinutes(
    selectedWorkoutExercises,
  );
  const beginDayAction = (action: "start" | "skip") => {
    if (dayActionInFlightRef.current) {
      return false;
    }

    dayActionInFlightRef.current = true;
    setPendingDayAction(action);
    return true;
  };
  const finishDayAction = () => {
    dayActionInFlightRef.current = false;
    setPendingDayAction(null);
  };
  useEffect(() => {
    if (!isWeekRouteActive) {
      return;
    }

    if (!program) {
      return;
    }

    if (!isValidWeekNumber) {
      nav.push(
        `/program/${enrollmentId}/week/${program.pointer_week}`,
        "none",
        "replace",
      );
      return;
    }

    if (isFutureWeek) {
      toast.message(FUTURE_WEEK_MESSAGE, {
        position: "top-center",
      });
      nav.push(
        `/program/${enrollmentId}/week/${program.pointer_week}`,
        "none",
        "replace",
      );
    }
  }, [
    enrollmentId,
    isFutureWeek,
    isValidWeekNumber,
    isWeekRouteActive,
    nav,
    program,
  ]);

  useEffect(() => {
    if (!isWeekRouteActive) {
      return;
    }

    if (!program || !isValidWeekNumber || weekDays.length === 0) {
      return;
    }

    const requestedDayValue = getRequestedDayValue(location.search);
    const fallbackDayValue =
      requestedWeekNumber === program.pointer_week
        ? getSafeDayValue(activeDayNum?.toString())
        : null;
    const nextDayValue =
      requestedDayValue ?? fallbackDayValue ?? weekDays[0].day_num.toString();
    const normalizedDayValue = weekDays.some(
      (day) => day.day_num.toString() === nextDayValue,
    )
      ? nextDayValue
      : weekDays[0].day_num.toString();

    if (requestedDayValue !== normalizedDayValue) {
      nav.push(
        `/program/${enrollmentId}/week/${requestedWeekNumber}?day=${normalizedDayValue}`,
        "none",
        "replace",
      );
    }

    setSelectedDayValue((currentDayValue) =>
      currentDayValue === normalizedDayValue
        ? currentDayValue
        : normalizedDayValue,
    );
  }, [
    data,
    isValidWeekNumber,
    isWeekRouteActive,
    location.search,
    program,
    requestedWeekNumber,
  ]);

  const openHistoryForWeekDay = (day: ProgramWeek["days"][number]) => {
    const workoutHistoryId = getWorkoutHistoryId(day);
    const historyUrl = `/program/${enrollmentId}/week/${weekNumber}/day/${day.day_num}/history`;

    nav.push(
      workoutHistoryId
        ? `${historyUrl}?workoutHistoryId=${workoutHistoryId}`
        : historyUrl,
    );
  };

  const handleDaySelect = (dayValue: string) => {
    const safeDayValue = getSafeDayValue(dayValue);
    if (!safeDayValue) {
      return;
    }

    setSelectedDayValue(safeDayValue);
    nav.push(
      `/program/${enrollmentId}/week/${weekNumber}?day=${safeDayValue}`,
      "none",
      "replace",
    );
  };

  const requiresStartConfirmation =
    Boolean(selectedWorkoutExercises) &&
    !isSelectedDayStarted &&
    !isSelectedDayHistorical &&
    isSelectedDayUnavailableFuture;
  const canSkipSelectedDay =
    Boolean(selectedDay) &&
    pendingDayAction === null &&
    isActiveDay &&
    !isSelectedDayFinished;
  const canUseStartButton =
    Boolean(selectedDayWorkout) &&
    pendingDayAction === null &&
    isActiveDay &&
    !isSelectedDayFinished;
  const unavailableActionButtonStyle =
    pendingDayAction === null
      ? ({
          cursor: "not-allowed",
          opacity: 0.45,
        } as const)
      : undefined;

  const showUnavailableActionMessage = () => {
    if (pendingDayAction !== null) {
      return;
    }

    if (!selectedDayWorkout) {
      toast.message(
        isSelectedDayToday
          ? REST_DAY_ACTION_MESSAGE
          : "В этот день нет тренировки — это день восстановления.",
        { position: "top-center" },
      );
      return;
    }

    if (isSelectedDayFinished) {
      toast.message("Этот день уже завершён", {
        position: "top-center",
      });
      return;
    }

    if (isSelectedDayUnavailableFuture) {
      toast.message(FUTURE_DAY_ACTION_MESSAGE, {
        position: "top-center",
      });
      return;
    }

    if (isSelectedDayPast) {
      toast.message("Этот день уже в истории программы", {
        position: "top-center",
      });
    }
  };

  const startWorkout = async (skipConfirmation = false) => {
    if (!selectedDayWorkout || isSelectedDayUnavailableFuture) {
      showUnavailableActionMessage();
      return;
    }

    if (isSelectedDayHistorical && selectedDayWorkout) {
      openHistoryForWeekDay(selectedDayWorkout);
      return;
    }

    if (requiresStartConfirmation && !skipConfirmation) {
      setIsStartWarningOpen(true);
      return;
    }

    if (!beginDayAction("start")) {
      return;
    }

    if (isSelectedDayStarted) {
      try {
        if (!selectedDayWorkout?.workout) {
          toast.error("Не удалось продолжить тренировку");
          return;
        }

        const workoutHistoryId =
          selectedDayWorkout.workout.history?.id_workout_history ??
          selectedDayWorkout.workout_history_id;

        if (!workoutHistoryId) {
          toast.error("Не удалось продолжить тренировку");
          return;
        }

        const localHistory =
          selectedDayWorkout.workout.history ??
          ({
            id_workout_history: workoutHistoryId,
            exercises_completed: 0,
          } as NonNullable<Training["history"]>);

        startTraining(
          { ...selectedDayWorkout.workout, history: localHistory },
          selectedDayRedirectUrl,
        );
        return;
      } finally {
        finishDayAction();
      }
    }

    try {
      const training = await ProgramExecutionProxy.startDay({
        enrollment_id: +enrollmentId,
        week_num: +weekNumber,
        day_num: +selectedDayValue,
      });

      if (!training) {
        toast.error("Не удалось начать тренировку");
        return;
      }

      void queryClient.invalidateQueries({ queryKey: programWeekQueryKey });
      void queryClient.invalidateQueries({
        queryKey: ["enrollment", enrollmentId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["main_page", "programs"],
      });
      scheduleTrainingListsInvalidation(queryClient);

      startTraining(training, selectedDayRedirectUrl);
    } finally {
      finishDayAction();
    }
  };

  const skipDay = async () => {
    if (!canSkipSelectedDay) {
      showUnavailableActionMessage();
      return;
    }

    if (!beginDayAction("skip")) {
      return;
    }

    try {
      const isSkipped = await ProgramExecutionProxy.skipDay({
        enrollment_id: +enrollmentId,
        week_num: +weekNumber,
        day_num: +selectedDayValue,
      });
      if (isSkipped) {
        toast.message("Тренировка в этот день пропущена", {
          position: "top-center",
        });
        const updatedProgram =
          await ProgramDataProxy.getEnrollmentById(+enrollmentId);

        if (updatedProgram) {
          queryClient.setQueryData(
            ["enrollment", enrollmentId],
            updatedProgram,
          );
        }

        void queryClient.invalidateQueries({ queryKey: programWeekQueryKey });
        void queryClient.invalidateQueries({
          queryKey: ["main_page", "programs"],
        });

        const nextDayNumber = Number(selectedDayValue) + 1;
        const nextDay = weekDays.find((d) => d.day_num === nextDayNumber);

        const nextWeekNumber = nextDay
          ? requestedWeekNumber
          : updatedProgram?.pointer_week ?? requestedWeekNumber;
        const nextDayValue = nextDay
          ? getSafeDayValue(nextDay.day_num.toString()) ?? selectedDayValue
          : getSafeDayValue(updatedProgram?.pointer_day?.toString()) ??
            selectedDayValue;

        if (nextWeekNumber === requestedWeekNumber) {
          setSelectedDayValue(nextDayValue);
        }

        nav.push(
          `/program/${enrollmentId}/week/${nextWeekNumber}?day=${nextDayValue}`,
          "none",
          "replace",
        );
      } else {
        toast.error("Не удалось пропустить тренировку", {
          position: "top-center",
        });
      }
    } finally {
      finishDayAction();
    }
  };

  if (!data) {
    return (
      <DefaultPage noPadding style={{ background: "#F8F8F3" }}>
        <main className="flex h-full w-full items-center justify-center bg-[#F8F8F3]">
          <CustomLoader />
        </main>
      </DefaultPage>
    );
  }

  return (
    <DefaultPage noPadding style={{ background: "#F8F8F3" }}>
      <Dialog open={isStartWarningOpen} onOpenChange={setIsStartWarningOpen}>
        <DialogContent className="w-[calc(100%-32px)] max-w-[380px] gap-0 rounded-[40px] border-0 bg-white px-5 pb-5 pt-7 shadow-[0_22px_64px_rgba(18,18,18,0.2)]">
          <DialogHeader className="pr-8 text-left">
            <DialogTitle className="text-balance text-[22px] font-black leading-[1.05] text-[#111111]">
              Вы уверены?
            </DialogTitle>
            <DialogDescription className="mt-3 text-pretty text-[15px] font-semibold leading-[1.35] text-[#626660]">
              {FUTURE_DAY_START_WARNING}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-7 grid grid-cols-2 gap-3">
            <BlueTransparentButton
              className="!h-[52px] !w-full !rounded-[20px] !text-[15px] !font-bold"
              onClick={() => setIsStartWarningOpen(false)}
              type="button"
            >
              Отмена
            </BlueTransparentButton>
            <YellowFilledButton
              className="!h-[52px] !w-full !rounded-[20px] !text-[15px] !font-bold"
              disabled={pendingDayAction !== null}
              onClick={() => {
                setIsStartWarningOpen(false);
                void startWorkout(true);
              }}
              type="button"
            >
              Начать
            </YellowFilledButton>
          </div>
        </DialogContent>
      </Dialog>
      <main className="h-full w-full max-w-full overflow-hidden bg-[#F8F8F3] text-[#111111]">
        <section className="relative flex h-full flex-col overflow-hidden">
          <ProgramCompactHeader
            isVisible={isCompactHeaderVisible}
            onBack={() => nav.push(ROUTES.overall.main)}
            title={programTitle}
            rightLabel={programFormat}
            details={
              <>
                <span className="shrink-0 rounded-[10px] bg-[#DDFB1F] px-2 py-1 text-[11px] font-black leading-none text-[#111111]">
                  {selectedWeekShortLabel}
                </span>
                <span className="min-w-0 truncate">
                  День {selectedDayValue}
                </span>
                <span className="size-1 shrink-0 rounded-full bg-[#C8CCC1]" />
                <span className="shrink-0">{clampedProgressPercentage}%</span>
              </>
            }
          />

          <div
            className="h-full overflow-y-auto overscroll-contain pb-[128px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            ref={compactHeaderRootRef}
          >
            <div className="relative">
              <ProgramCompactHeaderSentinel ref={compactHeaderSentinelRef} />
              <div className="relative z-0 h-[336px] overflow-hidden bg-[#F7F8F4]">
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    className="absolute bottom-0 left-[38%] right-[-42%] top-0 overflow-hidden rounded-bl-[72px]"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
                      maskImage:
                        "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
                    }}
                  >
                    <img
                      src={heroImage}
                      alt={programTitle}
                      className="h-full w-full object-cover object-[90%_center]"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-[24%] bg-[linear-gradient(180deg,rgba(247,248,244,0)_0%,rgba(247,248,244,0.68)_100%)]" />
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(248,248,243,0.02)_50%,rgba(248,248,243,0.42)_100%)]" />
                <div className="absolute left-5 top-[calc(22px+env(safe-area-inset-top))] z-30 flex flex-col items-start">
                  <button
                    type="button"
                    onClick={() => nav.push(ROUTES.overall.main)}
                    aria-label="Назад"
                    className="flex size-[48px] items-center justify-center overflow-hidden rounded-[18px] bg-[#F0F1EF] p-[3px] text-[#111111] ring-1 ring-black/[0.035] transition-transform duration-150 ease-out active:scale-[0.96]"
                  >
                    <ArrowLeft
                      size={25}
                      strokeWidth={2.45}
                      className="shrink-0"
                    />
                  </button>
                  <div className="mt-2 inline-flex h-8 items-center rounded-[12px] bg-[#DDFB1F] px-3 text-[14px] font-bold text-[#111111] shadow-[0_10px_24px_rgba(221,251,31,0.22)]">
                    {programFormat}
                  </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 z-20 px-5 pb-14">
                  <h1 className="line-clamp-2 max-w-[440px] text-[34px] font-extrabold leading-[1.02] tracking-[0] text-[#111111]">
                    {programTitle}
                  </h1>
                  <p className="mt-2 flex max-w-[440px] flex-wrap items-center text-[16px] font-medium leading-[1.25] text-[#30302C]">
                    <span className="inline-flex h-8 items-center rounded-[12px] bg-[#111111] px-3 text-[13px] font-black leading-none text-white shadow-[0_10px_24px_rgba(24,24,24,0.14)]">
                      {selectedWeekLabel}
                    </span>
                    <span className="ml-1.5">, день {selectedDayValue}</span>
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-[14px] font-medium text-[#252525]">
                    <Clock3 size={17} strokeWidth={2.1} />
                    <span>Осталось {daysLeft} дней</span>
                  </div>
                </div>
              </div>

              <div className="relative z-10 -mt-10 rounded-t-[34px] bg-[#fafafa] pt-[24px] shadow-[0_-14px_34px_rgba(20,20,20,0.045)]">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[34px] overflow-hidden rounded-t-[34px]"
                >
                  <div className="absolute inset-x-0 top-0 h-[34px] rounded-t-[34px] border-x border-t border-[#E1E4DC]" />
                  <div
                    className="absolute inset-x-0 top-0 h-[34px] rounded-t-[34px] border-x-[3px] border-t-[3px] border-[#DDFB1F]"
                    style={{
                      clipPath: `inset(0 ${progressBorderClipRight} 0 0)`,
                    }}
                  />
                </div>
                <div className="absolute right-5 top-[-30px] z-20 text-[14px] font-bold text-[#111111]/55">
                  {progressPercentage}%
                </div>
                <div className="relative z-10">
                  <WeekCalendar
                    week={safeWeekData}
                    selectedDayValue={selectedDayValue}
                    onDaySelect={handleDaySelect}
                  />

                  <div className="mx-5 mt-4 grid grid-cols-2 gap-3 rounded-[18px] bg-[#F0F0EA] p-3">
                    <div className="flex min-h-[52px] items-center gap-3 rounded-[15px] bg-[#fafafa] px-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DDFB1F] text-[#111111]">
                        <Clock3 size={18} strokeWidth={2.2} />
                      </span>
                      <span className="text-[14px] font-semibold leading-none">
                        {workoutDurationMinutes || 0} минут
                      </span>
                    </div>
                    <div className="flex min-h-[52px] items-center gap-3 rounded-[15px] bg-[#fafafa] px-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DDFB1F] text-[#111111]">
                        <Activity size={18} strokeWidth={2.2} />
                      </span>
                      <span className="text-[14px] font-semibold leading-none">
                        {selectedWorkoutExercises?.length ?? 0} упражнений
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative bg-[#fafafa] px-5 pt-6">
                {selectedDayWorkout === null ||
                selectedWorkoutExercises === null ? (
                  <div className="flex min-h-[300px] flex-col items-center justify-center rounded-[26px] bg-white px-7 text-center shadow-[0_16px_46px_rgba(24,24,24,0.06)]">
                    <div className="flex h-28 w-28 items-center justify-center rounded-full bg-[#F0F5DA]">
                      <img
                        src={RelaxImage}
                        alt="В этот день нет тренировок"
                        className="h-20 w-20 object-contain"
                      />
                    </div>
                    <h2 className="mt-5 text-[24px] font-black leading-none text-[#111111]">
                      День восстановления
                    </h2>
                    <p className="mt-3 text-[15px] font-medium leading-[1.35] text-[#6D6D68]">
                      В этот день нет тренировки. Можно восстановиться и
                      вернуться к следующему тренировочному дню.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between px-1">
                      <h2 className="text-[22px] font-black leading-none text-[#111111]">
                        {selectedWorkoutExercises.length} упражнений
                      </h2>
                      <button
                        type="button"
                        onClick={() =>
                          nav.push(`/program/${enrollmentId}/calendar`)
                        }
                        className="flex h-10 items-center gap-2 rounded-full bg-[#F0F0EA] px-4 text-[13px] font-bold text-[#1F1F1C] transition-transform duration-150 ease-out active:scale-[0.97]"
                      >
                        <CalendarDays size={16} strokeWidth={2.2} />
                        Все дни
                      </button>
                    </div>
                    <div className="flex flex-col gap-2.5">
                      {selectedWorkoutExercises.map((exercise, index) => {
                        const exerciseDetail = exercise.exercise_detail;
                        const exercisePreviewUrl = getExercisePreviewUrl(
                          exerciseDetail?.media,
                        );
                        const exerciseId = exerciseDetail?.id_exercise;
                        const exerciseName =
                          exerciseDetail?.name ?? "Упражнение";

                        return (
                          <button
                            type="button"
                            disabled={!exerciseId}
                            onClick={() => {
                              if (!exerciseId) return;
                              nav.push(`/exercise/${exerciseId}`);
                            }}
                            key={index}
                            className="group flex min-h-[102px] w-full items-center gap-3.5 rounded-[26px] bg-white p-3.5 pr-4 text-left shadow-[0_12px_30px_rgba(24,24,24,0.055)] transition-transform duration-200 ease-out active:scale-[0.985] disabled:cursor-default"
                          >
                            <div className="relative flex h-[74px] w-[112px] shrink-0 items-center justify-center overflow-visible rounded-[12px] bg-[#EFF7D5] p-2">
                              {exercisePreviewUrl ? (
                                <>
                                  <div className="absolute inset-2 rounded-[4px] bg-[#F8FCEB]/90" />
                                  <img
                                    src={exercisePreviewUrl}
                                    alt={exerciseName}
                                    className="relative z-10 h-[84px] w-[120px] max-w-none object-contain drop-shadow-[0_7px_9px_rgba(57,70,50,0.14)] transition-transform duration-300 ease-out group-active:scale-[0.98]"
                                  />
                                </>
                              ) : (
                                <div className="h-full w-full rounded-[4px] bg-[#EEF0EA]" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="line-clamp-2 text-[17px] font-bold leading-[1.12] text-[#151515]">
                                {exerciseName}
                              </div>
                              <div className="mt-1 text-[15px] font-bold leading-none text-[#5664FF]">
                                {getExerciseTarget(exercise)}
                              </div>
                            </div>
                            <ChevronRight
                              size={21}
                              strokeWidth={2.1}
                              className="shrink-0 text-[#272727]"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 z-20 bg-[linear-gradient(180deg,rgba(250,250,250,0)_0%,rgba(250,250,250,0.72)_46%,#fafafa_100%)] px-5 pb-[calc(14px+env(safe-area-inset-bottom))] pt-10">
              <div className="grid grid-cols-[1fr_1fr] gap-3">
                <BlueTransparentButton
                  className="!h-[58px] !w-full !rounded-[22px] !bg-white !text-[16px] !font-bold transition-transform duration-150 ease-out active:scale-[0.97]"
                  aria-disabled={!canSkipSelectedDay}
                  disabled={pendingDayAction !== null}
                  onClick={() => {
                    if (!canSkipSelectedDay) {
                      showUnavailableActionMessage();
                      return;
                    }
                    void skipDay();
                  }}
                  style={!canSkipSelectedDay ? unavailableActionButtonStyle : undefined}
                  type="button"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <CalendarDays size={20} strokeWidth={2.1} />
                    Пропустить
                  </span>
                </BlueTransparentButton>
                <YellowFilledButton
                  className="!h-[58px] !w-full !rounded-[22px] !text-[17px] !font-bold transition-transform duration-150 ease-out active:scale-[0.97]"
                  aria-disabled={!canUseStartButton}
                  disabled={pendingDayAction !== null}
                  onClick={() => {
                    if (!canUseStartButton || !selectedDayWorkout) {
                      showUnavailableActionMessage();
                      return;
                    }

                    if (isSelectedDayHistorical) {
                      openHistoryForWeekDay(selectedDayWorkout);
                      return;
                    }

                    if (selectedWorkoutExercises) {
                      void startWorkout();
                    }
                  }}
                  style={!canUseStartButton ? unavailableActionButtonStyle : undefined}
                  type="button"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {startWorkoutTitle}
                    <ArrowRight size={22} strokeWidth={2.1} />
                  </span>
                </YellowFilledButton>
              </div>
            </div>
        </section>
      </main>
    </DefaultPage>
  );
};
